import { useAuth } from '@clerk/react'
import { useCallback } from 'react'

/**
 * useApi — fetch helper that attaches the Clerk session token to every request.
 * Calls /api/* endpoints served by the Cloudflare Pages Function in functions/api/.
 * The Pages Function verifies the token and proxies to Supabase with service role.
 */
export function useApi() {
  const { getToken } = useAuth()

  const call = useCallback(
    async <T>(path: string, init: RequestInit = {}): Promise<T> => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const headers = new Headers(init.headers)
      headers.set('Authorization', `Bearer ${token}`)
      if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json')
      }

      const res = await fetch(`/api${path}`, { ...init, headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`API ${res.status} ${path}: ${text || res.statusText}`)
      }
      const ct = res.headers.get('content-type') ?? ''
      if (ct.includes('application/json')) {
        return (await res.json()) as T
      }
      return (await res.text()) as unknown as T
    },
    [getToken],
  )

  return { call }
}
